import { Copy, Eye, EyeSlash, SpinnerGap, X } from '@phosphor-icons/react'
import clsx from 'clsx'
import type { ClipboardEvent, FC, ReactNode, Ref } from 'react'
import React, { useEffect, useState } from 'react'

import { SpinnerIcon } from '@/components/shared/ui/Icons'
import { useTranslations } from '@/hooks/useTranslations'
import { copyClipboard } from '@/lib/helpers/appHelpers'

import Button from '../Button'
import Label from '../Label'
import InputStyles from './Input.module.css'

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  borderless?: boolean
  copy?: boolean
  copyTooltipText?: string
  defaultValue?: string | number
  descriptionText?: string
  isDisabled?: boolean
  readonly?: 'true' | 'false'
  inputRef?: Ref<any> | any
  name?: string
  label?: string
  maxLength?: number
  maxLengthText?: string
  minChartLength?: number
  minLengthText?: string
  prefixHelperText?: string
  prefixicon?: any
  rounded?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  suffixHelperText?: ReactNode
  suffixicon?: any
  type?: HTMLInputElement['type']
  autoComplete?: string
  autoFocus?: boolean
  clear?: boolean
  required?: boolean
  min?: number
  max?: number
  onClear?: () => void
  suffixOnClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  loading?: boolean
  className?: string
  selectToCopy?: boolean
  disableCopyAndPaste?: boolean
  dataTestId?: string
}

const Input: FC<InputProps> = ({
  autoComplete,
  borderless,
  autoFocus,
  copy,
  copyTooltipText,
  defaultValue,
  isDisabled = false,
  id,
  label,
  maxLength,
  maxLengthText,
  minChartLength = 0,
  minLengthText,
  name,
  onChange,
  placeholder,
  inputRef,
  prefixHelperText,
  prefixicon,
  rounded = 'md',
  size = 'sm',
  suffixHelperText,
  suffixicon,
  type = 'text',
  clear,
  value,
  required,
  onClear,
  loading = false,
  suffixOnClick,
  className,
  disableCopyAndPaste,
  dataTestId,
  ...props
}: InputProps) => {
  const [inputType, setInputType] = useState(type)
  const [characterCount, setCharacterCount] = useState({
    count: defaultValue ? defaultValue.toString().length : 0,
  })
  const t = useTranslations()
  const [inputText, setInputText] = useState(defaultValue?.toString())

  const inputClasses = [InputStyles['hnui-input']]
  // if (errortext) inputClasses.push(InputStyles['hnui-input--error'])
  if (size) inputClasses.push(InputStyles[`hnui-input--${size}`])
  if (rounded && !prefixHelperText && !suffixHelperText)
    inputClasses.push(InputStyles[`hnui-input-rounded-${rounded}`])
  if (prefixHelperText)
    inputClasses.push(InputStyles['hnui-input-helper-prefix'])
  if (suffixHelperText)
    inputClasses.push(InputStyles['hnui-input-helper-suffix'])
  inputClasses.push(InputStyles[`hnui-input-rounded-${rounded}`])
  if (copy && !suffixHelperText)
    inputClasses.push(InputStyles['hnui-input-helper-suffix'])
  inputClasses.push(InputStyles[`hnui-input-rounded-${rounded}`])
  if (borderless) inputClasses.push(InputStyles['hnui-input--borderless'])
  if (prefixHelperText) inputClasses.push(InputStyles['rounded-r-lg'])
  if (suffixHelperText) inputClasses.push(InputStyles['rounded-l-lg'])
  if (copy && !suffixHelperText) inputClasses.push(InputStyles['rounded-l-lg'])
  if (prefixicon) inputClasses.push(InputStyles['hnui-input-with-icon'])
  if (suffixicon) inputClasses.push(InputStyles['hnui-input-surfix-icon'])

  const iconLoaderClasses = [InputStyles['hnui-anim--spin']]
  if (loading) iconLoaderClasses.push(InputStyles[`hnui-loader--center`])

  if (type === 'password' && characterCount.count > 2)
    inputClasses.push(InputStyles['hnui-input-surfix-icon'])

  const copyHandler = (_data: any) => {
    if (inputText || copy) {
      copyClipboard(inputText || '', copyTooltipText || t('buttons.copied'))
    }
  }

  const checkCharacterCount = (input: any) => {
    setCharacterCount(() => {
      if (input) {
        return { count: input.length }
      }
      return { count: 0 }
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      Boolean(maxLength && e.target.value?.length <= maxLength) ||
      !maxLength
    ) {
      setInputText(e.target.value)
      if (onChange) onChange(e)
    }
  }

  const handleSuffixOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (suffixOnClick) suffixOnClick(e)
  }

  const handlePasteText = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disableCopyAndPaste) {
      e.preventDefault()
      return false
    }
    return true
  }

  useEffect(() => {
    checkCharacterCount(inputText)
  }, [inputText])

  useEffect(() => {
    setInputType(type)
  }, [type])

  useEffect(() => {
    if (value) setInputText(value.toString())
  }, [value])

  const renderLimits = () => {
    if (maxLength && characterCount.count >= maxLength) {
      return (
        <span
          className={`${InputStyles['hnui-input--errortext']} ${InputStyles['hnui-input--error']}`}
        >
          {maxLengthText}
        </span>
      )
    }
    if (characterCount.count <= minChartLength) {
      return (
        <span
          className={`${InputStyles['hnui-input--errortext']} ${InputStyles['hnui-input--error']}`}
        >
          {minLengthText}
        </span>
      )
    }
    return false
  }

  const renderShowPassword = () => {
    if (suffixicon && type !== 'password') {
      return (
        <span
          data-testid='ui_input_suffix_icon'
          className={`${InputStyles['hnui-input-icon-poiter']} ${InputStyles['hnui-input-suffix-icon']}`}
        >
          {suffixicon}
        </span>
      )
    }
    if (type === 'password' && characterCount.count > 2) {
      if (inputType === 'password') {
        return (
          <div className={InputStyles['hnui-input-password-show']}>
            <Button
              dataTestId='ui_input_password_toggle'
              as='span'
              variant='naked'
              size='xs'
              onClick={() => setInputType('text')}
              data-testid='ui_input_password_toggle'
            >
              <EyeSlash
                weight='bold'
                className={InputStyles['hnui-input-password-show-icon']}
              />
            </Button>
          </div>
        )
      }
      if (inputType === 'text') {
        return (
          <div className={InputStyles['hnui-input-password-show']}>
            <Button
              dataTestId='ui_input_password_toggle'
              as='span'
              variant='naked'
              size='xs'
              onClick={() => setInputType('password')}
            >
              <Eye
                weight='bold'
                className={InputStyles['hnui-input-password-show-icon']}
              />
            </Button>
          </div>
        )
      }
    }
    return false
  }

  const handleClearText = () => {
    if (onClear) onClear()
    setInputText('')
  }

  const renderClearIcon = () => {
    if (
      clear &&
      inputType !== 'password' &&
      inputText?.length &&
      inputRef?.current?.value.length
    )
      return (
        <div className={InputStyles['hnui-input-suffix-icon']}>
          {loading ? (
            <SpinnerIcon className='h-4 w-4' />
          ) : (
            <Button
              dataTestId='ui_input_clear_btn'
              variant='naked'
              onClick={handleClearText}
              className={InputStyles['hnui-input-suffix-icon']}
            >
              <X weight='bold' />
            </Button>
          )}
        </div>
      )
    return false
  }

  const renderInput = () => {
    return (
      <div className={InputStyles['hnui-input-flex-container']}>
        {prefixicon && (
          <span className={InputStyles['hnui-input-prefix-icon']}>
            {prefixicon}
          </span>
        )}
        <input
          data-testid={dataTestId}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={clsx(
            inputClasses.join(' '),
            isDisabled
              ? 'cursor-not-allowed opacity-40 hover:!border-gray6'
              : '',
            className
          )}
          defaultValue={defaultValue}
          disabled={isDisabled}
          id={id || label}
          name={name}
          onChange={handleChange}
          maxLength={maxLength}
          placeholder={placeholder || ''}
          ref={inputRef}
          type={inputType}
          value={value === null ? '' : value?.toString()}
          required={required}
          onPaste={handlePasteText}
          {...props}
        />
        {copy && !suffixHelperText && type !== 'password' && (
          <span
            className={clsx(
              InputStyles['hnui-input-suffix-helper'],
              InputStyles[`hnui-input--${size}`],
              isDisabled ? 'group !border-gray5/50 hover:!border-gray5/50 ' : ''
            )}
          >
            <button
              data-testid='ui_input_copy'
              className={clsx('p-[7px] hover:text-primary')}
              onClick={(e) => copyHandler(e)}
            >
              <Copy weight='duotone' />
            </button>
          </span>
        )}
        {renderShowPassword()}
        {renderClearIcon()}
      </div>
    )
  }

  const renderHelper = () => {
    if (prefixHelperText && prefixHelperText.length && !prefixicon) {
      return (
        <div
          className={clsx(
            'flex w-full',
            isDisabled
              ? 'cursor-not-allowed opacity-40 hover:!border-gray6'
              : ''
          )}
        >
          <span
            data-testid='ui_input_prefix'
            className={clsx(
              InputStyles['hnui-input-prefix-helper'],
              borderless ? '!border-none' : ''
            )}
          >
            {prefixHelperText}
          </span>
          {renderInput()}
        </div>
      )
    }
    if (suffixHelperText && !suffixicon) {
      return (
        <div
          className={clsx(
            'flex w-full',
            isDisabled ? 'opacity-40 hover:!border-gray6' : '',
            suffixOnClick ? 'opacity-100' : ''
          )}
        >
          {renderInput()}
          <button
            data-testid='input_suffix_text'
            className={clsx(
              InputStyles['hnui-input-suffix-helper'],
              borderless ? '!border-none !shadow-none' : '',
              suffixOnClick ? '!border-gray5 opacity-100' : 'cursor-default',
              isDisabled ? '!border-gray4 ' : ''
            )}
            onClick={(e) => handleSuffixOnClick(e)}
          >
            {loading ? (
              <SpinnerGap className={iconLoaderClasses.join(' ')} />
            ) : (
              <>{suffixHelperText}</>
            )}
          </button>
        </div>
      )
    }
    return false
  }

  return (
    <div className={InputStyles['hnui-input-container']}>
      {label && <Label htmlfor={label}>{label}</Label>}
      {!suffixHelperText && !prefixHelperText && renderInput()}
      {renderHelper()}
      {renderLimits()}
    </div>
  )
}

Input.displayName = 'Input'

export default Input
